import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Button,
  Label,
  MultiSelectCheckboxTag,
  Badge,
  SpinnerLoader,
} from 'lift-ui';
import { User, UserGroup } from '../../types';
import * as api from '../../api';
import { transformToLegacyUser } from '../../types';
import '../../iam-support.styles.scss';

interface EditGroupMembersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: UserGroup | null;
  users: User[];
  onSave: (groupId: number, toAdd: number[], toRemove: number[]) => void;
  isSubmitting?: boolean;
}

const EditGroupMembersSheet: React.FC<EditGroupMembersSheetProps> = ({
  open,
  onOpenChange,
  group,
  users: initialUsers,
  onSave,
  isSubmitting = false,
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const originalMemberIds = useMemo(() => {
    if (!group) return new Set<string>();
    return new Set((group.userIds || []).map(id => String(id)));
  }, [group]);

  useEffect(() => {
    if (open) {
      fetchAllUsers();
    }
  }, [open]);

  // Once users are loaded, pre-select only IDs that exist in the fetched user list
  useEffect(() => {
    if (allUsers.length > 0 && open) {
      const validIds = new Set(allUsers.map(u => String(u.id)));
      setSelectedUserIds(Array.from(originalMemberIds).filter(id => validIds.has(id)));
    }
  }, [allUsers, open, originalMemberIds]);

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const usersData = await api.getAllUsersWithPermissions();
      const legacyUsers = usersData.map(transformToLegacyUser);
      setAllUsers(legacyUsers);
    } catch (error) {
      console.error('Failed to fetch all users:', error);
      setAllUsers(initialUsers || []);
    } finally {
      setLoadingUsers(false);
    }
  };

  const options = useMemo(() => {
    const selected = new Set(selectedUserIds);
    return allUsers
      .map(user => ({
        label: `${user.name} (${user.email})`,
        value: String(user.id),
      }))
      .sort((a, b) => {
        const aSelected = selected.has(a.value) ? 0 : 1;
        const bSelected = selected.has(b.value) ? 0 : 1;
        return aSelected - bSelected;
      });
  }, [allUsers, selectedUserIds]);

  const { toAdd, toRemove, hasChanges } = useMemo(() => {
    const currentSet = new Set(selectedUserIds);
    const addIds = selectedUserIds.filter(id => !originalMemberIds.has(id));
    const removeIds = Array.from(originalMemberIds).filter(id => !currentSet.has(id));
    return {
      toAdd: addIds.map(id => parseInt(id, 10)),
      toRemove: removeIds.map(id => parseInt(id, 10)),
      hasChanges: addIds.length > 0 || removeIds.length > 0,
    };
  }, [selectedUserIds, originalMemberIds]);

  const handleSave = useCallback(() => {
    if (hasChanges && group) {
      onSave(group.id, toAdd, toRemove);
    }
  }, [hasChanges, group, toAdd, toRemove, onSave]);

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="md" className="iam-support-sheet flex flex-col h-full">
        <SheetHeader>
          <SheetTitle>Edit Members — {group?.name}</SheetTitle>
          <SheetDescription>
            Check users to add, uncheck to remove. Changes are applied when you save.
          </SheetDescription>
        </SheetHeader>
        <div className="iam-support-dialog-form flex-1 overflow-y-auto">
          {group && Object.keys(group.permissions).length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <Label className="text-sm text-blue-700 mb-2 block">
                Group Permissions:
              </Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(group.permissions).map(([product, level]) => (
                  <Badge key={product} variant="outline" className="bg-white">
                    {product}: {level}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Label>Members</Label>
            {loadingUsers ? (
              <div className="flex items-center justify-center py-4">
                <SpinnerLoader className="h-5 w-5" />
                <span className="ml-2 text-sm text-gray-500">Loading users...</span>
              </div>
            ) : options.length > 0 ? (
              <MultiSelectCheckboxTag
                placeholder="Search and select users..."
                value={selectedUserIds}
                onChange={setSelectedUserIds}
                options={options}
                containerClassName="w-full"
                showSelectAll
                tagMaxCharacters={35}
              />
            ) : (
              <p className="text-sm text-gray-500 py-2">
                No users available.
              </p>
            )}
          </div>
        </div>
        <SheetFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={!hasChanges || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default EditGroupMembersSheet;
